const DEMO_EMAIL = "actarad@yahoo.com";
const DEMO_PASSWORD = "ActaraDemo";
const DEMO_SEEN_INTRO_KEY = "demo_seen_intro";
const DEMO_BROWSER_ID_KEY = "demo_browser_id";
const DEMO_BROWSER_ID_LEGACY_KEYS = ["demoBrowserId", "browserSessionId", "demo_session_id"];
const DEMO_MODE_KEY = "demo_mode_enabled";
const DEMO_TEMP_PROJECT_BANNER_DISMISSED_PREFIX = "demo_temp_project_banner_dismissed:";
const DEMO_GENERATION_LOCK_TTL_MS = 10 * 60 * 1000;

function safeText(value) {
  return value == null ? "" : String(value);
}

function getMeta(project) {
  const meta = project && typeof project === "object" && project.meta && typeof project.meta === "object"
    ? project.meta
    : {};
  return meta;
}

export function isDemoEmail(email) {
  return safeText(email).trim().toLowerCase() === DEMO_EMAIL;
}

export function isSharedDemoUser(user) {
  if (user?.plan === "demo") return true;
  return isDemoEmail(user?.email || "");
}

export function getClientIdentityScope(user) {
  if (isSharedDemoUser(user)) {
    return `demo:${getDemoBrowserId()}`;
  }
  const userId = safeText(user?.id).trim();
  if (!userId && isDemoModeEnabled()) {
    return `demo:${getDemoBrowserId()}`;
  }
  return userId ? `user:${userId}` : "anonymous";
}

export function getDemoBrowserId() {
  if (typeof window === "undefined") return "demo-browser";
  try {
    const existing = window.localStorage.getItem(DEMO_BROWSER_ID_KEY);
    if (existing) return existing;
    for (const legacyKey of DEMO_BROWSER_ID_LEGACY_KEYS) {
      const legacyValue = window.localStorage.getItem(legacyKey);
      if (legacyValue) {
        window.localStorage.setItem(DEMO_BROWSER_ID_KEY, legacyValue);
        return legacyValue;
      }
    }
    const recoveredFromCache = recoverDemoBrowserIdFromProjectCache();
    if (recoveredFromCache) {
      window.localStorage.setItem(DEMO_BROWSER_ID_KEY, recoveredFromCache);
      return recoveredFromCache;
    }
    const next =
      (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `demo_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(DEMO_BROWSER_ID_KEY, next);
    return next;
  } catch (error) {
    void error;
    return `demo_${Date.now()}`;
  }
}

function recoverDemoBrowserIdFromProjectCache() {
  if (typeof window === "undefined") return "";
  try {
    const storage = window.localStorage;
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (typeof key === "string" && key.startsWith("ux-ai-projects-cache:demo:")) {
        keys.push(key);
      }
    }
    let bestId = "";
    let bestUpdatedAt = 0;
    keys.forEach((key) => {
      const raw = storage.getItem(key);
      if (!raw) return;
      let parsed = null;
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = null;
      }
      if (!Array.isArray(parsed)) return;
      parsed.forEach((project) => {
        if (!project || typeof project !== "object") return;
        if (project.isDemo !== true || project.isProtected === true) return;
        const candidateId = safeText(project.anonymousOwnerId || project?.meta?.anonymousOwnerId).trim();
        if (!candidateId) return;
        const updatedAt = Number(new Date(project.updatedAt || project.createdAt || 0).getTime()) || 0;
        if (!bestId || updatedAt >= bestUpdatedAt) {
          bestId = candidateId;
          bestUpdatedAt = updatedAt;
        }
      });
    });
    return bestId;
  } catch {
    return "";
  }
}

export function hasSeenDemoIntro() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(DEMO_SEEN_INTRO_KEY) === "true";
  } catch (error) {
    void error;
    return false;
  }
}

export function markDemoIntroSeen() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DEMO_SEEN_INTRO_KEY, "true");
  } catch (error) {
    void error;
  }
}

export function hasDismissedTempDemoProjectBanner(projectId) {
  const keyPart = safeText(projectId).trim();
  if (!keyPart || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(`${DEMO_TEMP_PROJECT_BANNER_DISMISSED_PREFIX}${keyPart}`) === "true";
  } catch {
    return false;
  }
}

export function dismissTempDemoProjectBanner(projectId) {
  const keyPart = safeText(projectId).trim();
  if (!keyPart || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${DEMO_TEMP_PROJECT_BANNER_DISMISSED_PREFIX}${keyPart}`, "true");
  } catch {}
}

export function isDemoModeEnabled() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(DEMO_MODE_KEY) === "true";
  } catch {
    return false;
  }
}

export function enableDemoMode() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DEMO_MODE_KEY, "true");
  } catch {}
}

export function disableDemoMode() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DEMO_MODE_KEY);
  } catch {}
}

export function getDemoProjectState(project, browserId = getDemoBrowserId()) {
  const meta = getMeta(project);
  const demoGenerationInProgress = Boolean(meta.demoGenerationInProgress);
  const demoGenerationBrowserId = safeText(meta.demoGenerationBrowserId).trim();
  const generationAt = Number(meta.demoGenerationStartedAt || meta.demoGenerationHeartbeat || 0);
  const generationFresh = Boolean(
    demoGenerationInProgress &&
      Number.isFinite(generationAt) &&
      generationAt > 0 &&
      Date.now() - generationAt <= DEMO_GENERATION_LOCK_TTL_MS
  );

  return {
    occupied: false,
    owned: false,
    blockedByOther: false,
    stale: false,
    generationActive: generationFresh,
    generationOwned: generationFresh && demoGenerationBrowserId === browserId,
    generationBlocked: generationFresh && demoGenerationBrowserId !== browserId,
    generationStale: demoGenerationInProgress && !generationFresh,
    meta,
  };
}

export function buildDemoGenerationMeta(browserId = getDemoBrowserId(), at = Date.now()) {
  return {
    demoGenerationInProgress: true,
    demoGenerationStartedAt: at,
    demoGenerationHeartbeat: at,
    demoGenerationBrowserId: browserId,
  };
}

export function clearDemoGenerationMeta() {
  return {
    demoGenerationInProgress: false,
    demoGenerationStartedAt: null,
    demoGenerationHeartbeat: null,
  };
}

export function applyDemoOccupancyToProject(project, browserId = getDemoBrowserId(), at = Date.now()) {
  void browserId;
  void at;
  return project;
}

export function applyDemoGenerationToProject(project, browserId = getDemoBrowserId(), at = Date.now()) {
  if (!project || typeof project !== "object") return project;
  return {
    ...project,
    meta: {
      ...(getMeta(project) || {}),
      ...buildDemoGenerationMeta(browserId, at),
    },
  };
}

export function clearDemoGenerationFromProject(project) {
  if (!project || typeof project !== "object") return project;
  const meta = { ...(getMeta(project) || {}) };
  delete meta.demoGenerationInProgress;
  delete meta.demoGenerationStartedAt;
  delete meta.demoGenerationHeartbeat;
  delete meta.demoGenerationBrowserId;
  return {
    ...project,
    meta,
  };
}

export function isDemoProjectOccupiedByOther(project, browserId = getDemoBrowserId()) {
  void project;
  void browserId;
  return false;
}

export function isDemoProjectGenerationBlocked(project, browserId = getDemoBrowserId()) {
  return getDemoProjectState(project, browserId).generationBlocked;
}

export { DEMO_EMAIL, DEMO_PASSWORD, DEMO_SEEN_INTRO_KEY, DEMO_MODE_KEY };
